import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  Heading,
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  HStack,
  useToast,
  Spinner,
  Text,
} from '@chakra-ui/react';

const Reports = ({ isAuthenticated, onNotAuthenticated }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCases: 0,
    activeCases: 0,
    completedCases: 0,
    closedCases: 0,
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchReportData();
    } else {
      onNotAuthenticated();
    }
  }, [isAuthenticated]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const casesUrl = window.location.origin === baseApiUrl || window.location.origin.includes('localhost') || window.location.origin.includes('127.0.0.1') ?
        '/cases.php' :
        `${baseApiUrl}/cases.php`;

      const response = await fetch(casesUrl, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data.cases)) {
        // Calculate statistics
        const cases = data.cases;
        const totalCases = cases.length;
        
        // Active cases: Candidate, Candidate: Wild Foster, Investigation, R&R in Process
        const activeStatuses = ['Candidate', 'Candidate: Wild Foster', 'Investigation', 'R&R in Process'];
        const activeCases = cases.filter(c => activeStatuses.includes(c.status)).length;
        
        // Completed cases: All terminal/closed statuses (successful outcomes)
        const completedStatuses = ['Renest Successful', 'Foster Successful'];
        const completedCases = cases.filter(c => completedStatuses.includes(c.status)).length;
        
        // Closed cases: All terminal statuses (both successful and unsuccessful)
        const closedStatuses = ['Renest Successful', 'Foster Successful', 'Renest/Foster Unsuccessful', 'Patient HE/DIC', 'Not a Candidate'];
        const closedCases = cases.filter(c => closedStatuses.includes(c.status)).length;

        console.log('Calculated stats:', { totalCases, activeCases, completedCases, closedCases });

        setStats({
          totalCases,
          activeCases,
          completedCases,
          closedCases,
        });
      } else {
        console.error('Invalid response format - no cases array:', data);
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast({
        title: 'Error loading reports',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

;

  if (loading) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)">
        <Container maxW="7xl" centerContent py={12}>
          <Spinner size="lg" color="purple.500" />
          <Text mt={4}>Loading reports...</Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)">
      <Container maxW="7xl" px={6} py={8}>
        <VStack align="stretch" spacing={8}>
          {/* Header */}
          <Box>
            <Heading
              as="h1"
              size={{ base: 'lg', md: '2xl' }}
              mb={2}
              color="gray.900"
              fontWeight="700"
            >
              Reports
            </Heading>
            <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600">
              Overview of case management statistics and metrics
            </Text>
          </Box>

          {/* Statistics Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              borderLeft="4px solid"
              borderColor="blue.500"
            >
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Total Cases
                </StatLabel>
                <StatNumber fontSize="2xl" color="blue.600">
                  {stats.totalCases}
                </StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  All time
                </StatHelpText>
              </Stat>
            </Box>

            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              borderLeft="4px solid"
              borderColor="green.500"
            >
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Active Cases
                </StatLabel>
                <StatNumber fontSize="2xl" color="green.600">
                  {stats.activeCases}
                </StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  In progress
                </StatHelpText>
              </Stat>
            </Box>

            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              borderLeft="4px solid"
              borderColor="purple.500"
            >
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Completed Cases
                </StatLabel>
                <StatNumber fontSize="2xl" color="purple.600">
                  {stats.completedCases}
                </StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  Successful outcomes
                </StatHelpText>
              </Stat>
            </Box>

            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              borderLeft="4px solid"
              borderColor="red.500"
            >
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Closed Cases
                </StatLabel>
                <StatNumber fontSize="2xl" color="red.600">
                  {stats.closedCases}
                </StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  All closed (successful & unsuccessful)
                </StatHelpText>
              </Stat>
            </Box>

          </SimpleGrid>

          {/* Action Buttons */}
          <HStack spacing={4}>
            <Button
              variant="outline"
              borderColor="purple.500"
              color="purple.600"
              _hover={{ bg: "purple.50", transform: 'translateY(-2px)' }}
              fontWeight="600"
              size="md"
              onClick={fetchReportData}
            >
              Refresh Data
            </Button>
          </HStack>

          {/* Placeholder for future charts/graphs */}
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="sm"
            textAlign="center"
          >
            <Text color="gray.500" fontSize="sm">
              Additional visualizations coming soon...
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Reports;
